/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { AppstoreOutlined } from '@ant-design/icons';
import { useState } from 'react';

import { GetSubtenantByIdResponse, Subtenant } from '@/client';
import { BackButton } from '@/components/Buttons/BackButton';
import { ContentContainer } from '@/components/ContentContainer';
import { GuardedButton } from '@/components/GuardedButton';
import { PageHeader } from '@/components/PageHeader';
import { QueryWrapper } from '@/components/QueryWrapper';
import { usePaginatedNodeConfigsQuery } from '@/hooks/query/usePaginatedNodeConfigsQuery';
import { usePaginatedNodesQuery } from '@/hooks/query/usePaginatedNodesQuery';
import { usePaginatedSubtenantMembersQuery } from '@/hooks/query/usePaginatedSubtenantMembersQuery';
import { useSubtenantByIdQuery } from '@/hooks/query/useSubtenantByIdQuery';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { SubtenantConfigs } from '@/pages/SubtenantDetailsPage/SubtenantConfigs';
import { SubtenantMembers } from '@/pages/SubtenantDetailsPage/SubtenantMembers';
import { SubtenantNodes } from '@/pages/SubtenantDetailsPage/SubtenantNodes';
import { DeleteSubtenantModal } from '@/pages/Subtenants/DeleteSubtenantModal';
import { UpdateSubtenantModal } from '@/pages/Subtenants/UpdateSubtenantModal';
import { Container } from '@/primitives/Container';
import { Flex } from '@/primitives/Flex';
import { SubtenantBadge } from '@/primitives/SubtenantBadge';
import { Text } from '@/primitives/Text';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export type SubtenantDetailsPageProps = {
  subtenantId: string;
};

export function SubtenantDetailsPage({ subtenantId }: SubtenantDetailsPageProps) {
  const { t } = useMimicTranslation('subtenants');
  const tenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const tenantName = useMgmtPlaneStateStore((state) => state.selectedTenantName);
  const [editSubtenantOpen, setEditSubtenantOpen] = useState<boolean>(false);
  const [deleteSubtenant, setDeleteSubtenant] = useState<Subtenant>();
  const [deleteIsPending, setDeleteIsPending] = useState(false);
  const subtenantQuery = useSubtenantByIdQuery({ subtenantId });

  const nodesQuery = usePaginatedNodesQuery({
    params: {
      page: 1,
      pageSize: 5,
      filters: {
        subtenantId,
      },
    },
  });

  const configsQuery = usePaginatedNodeConfigsQuery({
    params: {
      page: 1,
      pageSize: 5,
      filters: {
        subtenantId,
      },
    },
  });

  const membersQuery = usePaginatedSubtenantMembersQuery({
    params: {
      id: subtenantId,
      page: 1,
      pageSize: 5,
    },
  });

  const deleteDisabled = (nodesQuery.data?.data?.length ?? 0) > 0;

  const editSubtenant = () => {
    setEditSubtenantOpen(true);
  };

  const delSubtenant = () => {
    setDeleteSubtenant(subtenantQuery.data?.data);
  };

  const onCloseEdit = () => {
    setEditSubtenantOpen(false);
  };

  const closeDeleteDialog = () => {
    setDeleteSubtenant(undefined);
    setDeleteIsPending(false);
  };

  return (
    <QueryWrapper<GetSubtenantByIdResponse> queryResult={subtenantQuery}>
      {({ data: subtenant }) => (
        <ContentContainer width="100%">
          <Container style={{ marginBottom: '1.5em' }}>
            <BackButton to={`/tenants/${tenantId}/subtenants`} label={t('actions.backToSubtenants')} />
          </Container>
          <Container>
            <PageHeader
              icon={<AppstoreOutlined style={{ width: '18px', height: '18px' }} />}
              title={
                <Flex gap="sm" align="center">
                  {subtenant.name}
                  <Text data-testid="subtenant-name" size="md" style={{ fontWeight: 400 }} mono copyable>
                    {subtenant.id}
                  </Text>
                </Flex>
              }
              level={3}
              fontWeight="600"
            >
              <Container justify="end" full style={{ marginRight: '16px' }}>
                <Container gap="sm" justify="flex-end" horizontal style={{ flexWrap: 'wrap' }}>
                  <Flex vertical align="center" gap="xxs">
                    <GuardedButton
                      requiredRole="admin"
                      type="primary"
                      dataTestId="delete-subtenant"
                      onClick={delSubtenant}
                      className="subtenant-button delete-button red"
                      disabled={deleteDisabled}
                    >
                      {t('actions.deleteSubtenant')}
                    </GuardedButton>
                    {deleteDisabled && (
                      <Text data-testid="delete-subtenant-disabled" size="xs">
                        {t('deleteSubtenant.disableDelete')}
                      </Text>
                    )}
                  </Flex>
                  <GuardedButton
                    requiredRole="admin"
                    type="primary"
                    dataTestId="edit-subtenant"
                    onClick={editSubtenant}
                    className="subtenant-button"
                  >
                    {t('actions.editSubtenantButton')}
                  </GuardedButton>
                </Container>
              </Container>
            </PageHeader>
          </Container>
          <Container style={{ marginTop: '24px' }} horizontal>
            <Container style={{ flex: 1, marginRight: '24px' }} gap="xs">
              <Text type="subtle" size="sm" style={{ display: 'block', fontWeight: 400 }}>
                {t('description')}
              </Text>
              <Text size="md">{subtenant.description || t('noDescriptionProvided')}</Text>
            </Container>
            <Container style={{ flex: 1 }} gap="xs">
              <Text type="subtle" size="sm" style={{ display: 'block', fontWeight: 400 }}>
                {t('parentSubtenant')}
              </Text>
              {subtenant.parent ? (
                <SubtenantBadge>{subtenant.parent.name}</SubtenantBadge>
              ) : (
                <Text size="md">{tenantName}</Text>
              )}
            </Container>
          </Container>
          <SubtenantNodes nodesQuery={nodesQuery} />
          <SubtenantConfigs configsQuery={configsQuery} />
          <SubtenantMembers subtenant={subtenant} membersQuery={membersQuery} />
          {editSubtenantOpen && <UpdateSubtenantModal subtenant={subtenant} onClose={onCloseEdit} />}
          <DeleteSubtenantModal
            deleteSubtenant={deleteSubtenant}
            deleteIsPending={deleteIsPending}
            setDeleteIsPending={setDeleteIsPending}
            onCancelSubtenantDelete={closeDeleteDialog}
          />
        </ContentContainer>
      )}
    </QueryWrapper>
  );
}
